/**
 * Core Web Vitals Monitoring
 * Tracks LCP, INP, CLS, FCP, TTFB per Light QA Contract standards
 * 
 * Thresholds (Google Web Vitals):
 * - LCP: < 2.5s (Good), 2.5-4s (Needs Improvement), > 4s (Poor)
 * - INP: < 200ms (Good), 200-500ms (Needs Improvement), > 500ms (Poor)
 * - CLS: < 0.1 (Good), 0.1-0.25 (Needs Improvement), > 0.25 (Poor)
 * - FCP: < 1.8s (Good), 1.8-3s (Needs Improvement), > 3s (Poor)
 * - TTFB: < 800ms (Good), 800-1800ms (Needs Improvement), > 1800ms (Poor)
 */

import { onLCP, onINP, onCLS, onFCP, onTTFB, type Metric } from 'web-vitals';
import { kpiTracker } from './kpiTracker';
import { logger } from './structuredLogger';

export const WEB_VITALS_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },    // 2.5s / 4s
  INP: { good: 200, poor: 500 },      // 200ms / 500ms
  CLS: { good: 0.1, poor: 0.25 },     // 0.1 / 0.25
  FCP: { good: 1800, poor: 3000 },    // 1.8s / 3s
  TTFB: { good: 800, poor: 1800 }     // 800ms / 1.8s
} as const;

export interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  navigationType?: string;
}

class CoreWebVitalsMonitor {
  private metrics: WebVitalMetric[] = [];
  private maxHistorySize = 50;
  private alertCallbacks: ((metric: WebVitalMetric) => void)[] = [];

  /**
   * Report metric to all monitoring systems
   */
  private reportMetric(metric: Metric): void {
    const { name, value, rating, navigationType } = metric;
    const metricName = name as keyof typeof WEB_VITALS_THRESHOLDS;
    
    // Record to KPI tracker with threshold
    const threshold = WEB_VITALS_THRESHOLDS[metricName];
    if (threshold) {
      kpiTracker.recordKPI(metricName, value, { threshold: threshold.good });
    }

    // Store in history
    const webVitalMetric: WebVitalMetric = {
      name,
      value,
      rating,
      timestamp: Date.now(),
      navigationType: navigationType as string
    };

    this.metrics.push(webVitalMetric);
    if (this.metrics.length > this.maxHistorySize) {
      this.metrics.shift();
    }

    // Log if threshold violated
    if (rating === 'poor') {
      logger.error('PERFORMANCE', `${name} = ${value.toFixed(2)}ms (POOR)`, {
        metric: name,
        value: Math.round(value),
        threshold: threshold?.good,
        rating,
        navigationType
      });

      // Trigger alert callbacks
      this.alertCallbacks.forEach(callback => callback(webVitalMetric));
    } else if (rating === 'needs-improvement') {
      logger.warn('PERFORMANCE', `${name} = ${value.toFixed(2)}ms (NEEDS IMPROVEMENT)`, {
        metric: name,
        value: Math.round(value),
        threshold: threshold?.good,
        rating,
        navigationType
      });
    } else {
      logger.debug('PERFORMANCE', `${name} = ${value.toFixed(2)}ms (GOOD)`, {
        metric: name,
        value: Math.round(value),
        threshold: threshold?.good,
        rating,
        navigationType
      });
    }
  }

  /**
   * Initialize Core Web Vitals tracking
   * Call this once in App.tsx during app initialization
   */
  initTracking(): void {
    try {
      onLCP((metric) => this.reportMetric(metric), { reportAllChanges: true });
      onINP((metric) => this.reportMetric(metric), { reportAllChanges: true });
      onCLS((metric) => this.reportMetric(metric), { reportAllChanges: true });
      onFCP((metric) => this.reportMetric(metric));
      onTTFB((metric) => this.reportMetric(metric));

      logger.info('PERFORMANCE', 'Core Web Vitals tracking initialized', {
        metrics: ['LCP', 'INP', 'CLS', 'FCP', 'TTFB'],
        thresholds: WEB_VITALS_THRESHOLDS
      });
    } catch (error) {
      logger.error('PERFORMANCE', 'Failed to initialize Core Web Vitals tracking', { error });
    }
  }

  /**
   * Get recent metrics history
   */
  getMetrics(limit = 20): WebVitalMetric[] {
    return this.metrics.slice(-limit);
  }

  /**
   * Get latest value for a specific metric
   */
  getLatestMetric(name: string): WebVitalMetric | null {
    for (let i = this.metrics.length - 1; i >= 0; i--) {
      if (this.metrics[i].name === name) {
        return this.metrics[i];
      }
    }
    return null;
  }

  /**
   * Get summary report of all metrics
   */
  getSummary(): {
    lcp: { value: number; rating: string } | null;
    inp: { value: number; rating: string } | null;
    cls: { value: number; rating: string } | null;
    fcp: { value: number; rating: string } | null;
    ttfb: { value: number; rating: string } | null;
    violationCount: number;
    totalMetrics: number;
  } {
    const lcp = this.getLatestMetric('LCP');
    const inp = this.getLatestMetric('INP');
    const cls = this.getLatestMetric('CLS');
    const fcp = this.getLatestMetric('FCP');
    const ttfb = this.getLatestMetric('TTFB');

    const violationCount = this.metrics.filter(m => m.rating === 'poor').length;

    return {
      lcp: lcp ? { value: lcp.value, rating: lcp.rating } : null,
      inp: inp ? { value: inp.value, rating: inp.rating } : null,
      cls: cls ? { value: cls.value, rating: cls.rating } : null,
      fcp: fcp ? { value: fcp.value, rating: fcp.rating } : null,
      ttfb: ttfb ? { value: ttfb.value, rating: ttfb.rating } : null,
      violationCount,
      totalMetrics: this.metrics.length
    };
  }

  /**
   * Subscribe to poor metric alerts
   */
  onAlert(callback: (metric: WebVitalMetric) => void): () => void {
    this.alertCallbacks.push(callback);
    return () => {
      const index = this.alertCallbacks.indexOf(callback);
      if (index > -1) {
        this.alertCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Clear all metrics history
   */
  clearMetrics(): void {
    this.metrics = [];
    logger.info('PERFORMANCE', 'Core Web Vitals metrics history cleared');
  }

  /**
   * Check if any metric violates Light QA Contract thresholds
   */
  hasViolations(): boolean {
    const summary = this.getSummary();
    
    // Check LCP < 2.5s
    if (summary.lcp && summary.lcp.value > WEB_VITALS_THRESHOLDS.LCP.good) {
      return true;
    }
    
    // Check INP < 200ms
    if (summary.inp && summary.inp.value > WEB_VITALS_THRESHOLDS.INP.good) {
      return true;
    }
    
    // Check CLS < 0.1
    if (summary.cls && summary.cls.value > WEB_VITALS_THRESHOLDS.CLS.good) {
      return true;
    }

    return false;
  }
}

// Export singleton instance
export const coreWebVitals = new CoreWebVitalsMonitor();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).coreWebVitals = coreWebVitals;
}
